import { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment/moment";
import * as patientApi from "../../service/patientApiDev";
import { useSearchParams, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import logo from "../../asset/image/new/logo.png";

const MOTHER_CHILD_FILTERS = [
  { key: "", value: "ทั้งหมด" },
  { key: "ดาวน์ซินโดรม", value: "ดาวน์ซินโดรม" },
  { key: "ธาลัสซีเมีย", value: "ธาลัสซีเมีย" },
  { key: "G6PD", value: "G6PD" },
  { key: "ทารกแรกเกิด", value: "ทารกแรกเกิด" },
];

const MOCK_MOTHER_CHILD_DATA = [
  {
    _id: "mock_01",
    expr2: "คัดกรองดาวน์ซินโดรม (ดาวน์ซินโดรม)",
    report_date: "2024-08-15T00:00:00.000Z",
    collect_date: "2024-08-12T00:00:00.000Z",
    customer_name: "โรงพยาบาลศรีหานาทร",
    lab_classify: {
      lab_result: {
        result: {
          result_name: "NEGATIVE",
          testing_result: "ไม่พบความเสี่ยงดาวน์ซินโดรม (ค่า MoM ปกติ)",
          recommend_patient: ["ควรติดตามผลการตรวจคัดกรองตามนัด", "หากมีข้อสงสัยควรปรึกษาแพทย์"],
        },
        method: { method_name: "Triple Test (AFP, uE3, hCG)" },
        objective: "คัดกรองความเสี่ยงดาวน์ซินโดรมในหญิงตั้งครรภ์",
      },
    },
  },
  {
    _id: "mock_02",
    expr2: "ตรวจยีนธาลัสซีเมีย (ธาลัสซีเมีย)",
    report_date: "2024-07-20T00:00:00.000Z",
    collect_date: "2024-07-18T00:00:00.000Z",
    customer_name: "โรงพยาบาลศรีหานาทร",
    lab_classify: {
      lab_result: {
        result: {
          result_name: "POSITIVE",
          testing_result: "พบพาหะธาลัสซีเมีย ชนิด Alpha-thalassemia 1 (SEA deletion)",
          recommend_patient: [
            "แนะนำให้คู่สมรสตรวจเพิ่มเติม",
            "หากคู่สมรสเป็นพาหะควรพบแพทย์เพื่อรับคำปรึกษาทางพันธุกรรม",
          ],
        },
        method: { method_name: "PCR / Gap-PCR" },
        objective: "ตรวจหาพาหะและผู้ป่วยธาลัสซีเมียในหญิงตั้งครรภ์และคู่สมรส",
      },
    },
  },
  {
    _id: "mock_03",
    expr2: "ตรวจภาวะพร่องเอนไซม์ G6PD (G6PD)",
    report_date: "2024-07-20T00:00:00.000Z",
    collect_date: "2024-07-18T00:00:00.000Z",
    customer_name: "โรงพยาบาลศรีหานาทร",
    lab_classify: {
      lab_result: {
        result: {
          result_name: "NEGATIVE",
          testing_result: "ระดับเอนไซม์ G6PD ปกติ (8.2 U/gHb)",
          recommend_patient: [],
        },
        method: { method_name: "Fluorescent Spot Test / Quantitative assay" },
        objective: "คัดกรองภาวะพร่องเอนไซม์ G6PD ในทารกแรกเกิด",
      },
    },
  },
  {
    _id: "mock_04",
    expr2: "คัดกรองทารกแรกเกิด TSH (ทารกแรกเกิด)",
    report_date: "2024-09-01T00:00:00.000Z",
    collect_date: "2024-08-30T00:00:00.000Z",
    customer_name: "โรงพยาบาลศรีหานาทร",
    lab_classify: {
      lab_result: {
        result: {
          result_name: "NEGATIVE",
          testing_result: "ค่า TSH = 2.1 mU/L (ปกติ < 5.0 mU/L)",
          recommend_patient: ["ผลการตรวจอยู่ในเกณฑ์ปกติ ไม่จำเป็นต้องติดตามเพิ่มเติม"],
        },
        method: { method_name: "Time-Resolved Fluoroimmunoassay (TRFIA)" },
        objective: "คัดกรองภาวะพร่องฮอร์โมนไทรอยด์แต่กำเนิดในทารกแรกเกิด",
      },
    },
  },
  {
    _id: "mock_05",
    expr2: "คัดกรองทารกแรกเกิด PKU (ทารกแรกเกิด)",
    report_date: "2024-09-01T00:00:00.000Z",
    collect_date: "2024-08-30T00:00:00.000Z",
    customer_name: "โรงพยาบาลศรีหานาทร",
    lab_classify: {
      lab_result: {
        result: {
          result_name: "NEGATIVE",
          testing_result: "ระดับ Phenylalanine = 1.2 mg/dL (ปกติ < 4.0 mg/dL)",
          recommend_patient: [],
        },
        method: { method_name: "Tandem Mass Spectrometry (MS/MS)" },
        objective: "คัดกรองโรค Phenylketonuria (PKU) ในทารกแรกเกิด",
      },
    },
  },
];


const getResultStyle = (resultName) => {
  if (!resultName) return { bg: "bg-gray-400", text: "text-white" };
  const upper = resultName.toUpperCase();
  if (upper.includes("POSITIVE") || upper.includes("SLOW") || upper.includes("RAPID")) {
    return { bg: "bg-red-500", text: "text-white" };
  }
  if (upper.includes("NEGATIVE")) {
    return { bg: "bg-green-700", text: "text-white" };
  }
  if (upper.includes("INTERMEDIATE")) {
    return { bg: "bg-blue-600", text: "text-white" };
  }
  return { bg: "bg-gray-500", text: "text-white" };
};

function ResultCard({ item, index, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const resultName = item?.lab_classify?.lab_result?.result?.result_name || "";
  const style = getResultStyle(resultName);

  return (
    <div className={`overflow-hidden bg-white border border-t-0 border-gray-100 ${isLast ? "rounded-b-xl mb-5" : "mb-0"} shadow-sm`}>
      {/* Card row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Number circle */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2"
          style={{ borderColor: "#0d9488" }}
        >
          <span className="font-bold text-sm" style={{ color: "#0d9488" }}>{index + 1}</span>
        </div>

        {/* Title + Date */}
        <div className="flex-1 min-w-0">
          <p className="text-gray-800 font-semibold text-sm leading-snug">{item.expr2 || "-"}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {item.report_date
              ? moment(item.report_date).add(543, "years").format("DD MMM YYYY")
              : "-"}
          </p>
        </div>

        {/* Badge + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${style.bg} ${style.text}`}>
            {resultName || "ไม่มีข้อมูล"}
          </span>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="text-gray-400 text-xs"
          />
        </div>
      </div>

      {/* Expanded Detail */}
      <div
        style={{
          maxHeight: expanded ? "800px" : "0px",
          opacity: expanded ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 0.4s ease, opacity 0.3s ease",
        }}
      >
        <div className="bg-gray-50 border-t border-gray-100 px-4 pb-4 pt-3 text-sm space-y-2">
          <DetailRow label="ผลการตรวจ" value={item?.lab_classify?.lab_result?.result?.testing_result} />
          <DetailRow label="หน่วยงานที่ส่งตรวจ" value={item.customer_name} />
          <DetailRow
            label="วันที่เก็บตัวอย่าง"
            value={item.collect_date ? moment(item.collect_date).add(543, "years").format("DD MMMM YYYY") : "-"}
          />
          <DetailRow
            label="วันที่รายงานผล"
            value={item.report_date ? moment(item.report_date).add(543, "years").format("DD MMMM YYYY") : "-"}
          />
          <DetailRow label="วิธีการตรวจ" value={item?.lab_classify?.lab_result?.method?.method_name} />
          {item?.lab_classify?.lab_result?.objective && (
            <DetailRow label="วัตถุประสงค์" value={item.lab_classify.lab_result.objective} />
          )}
          {item?.lab_classify?.lab_result?.result?.recommend_patient?.length > 0 && (
            <div className="mt-3 p-3 bg-teal-50 rounded-lg">
              <p className="text-xs font-bold text-teal-700 mb-1">คำแนะนำ</p>
              {item.lab_classify.lab_result.result.recommend_patient.map((r, i) => (
                <p key={i} className="text-xs text-teal-800">{r}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-gray-500 w-32 flex-shrink-0">{label}</span>
      <span className="text-gray-800 font-medium flex-1">{value || "-"}</span>
    </div>
  );
}

function MiniAppLaboratoryResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appToken = searchParams.get("token");
  const directViewerToken = searchParams.get("viewer_token");

  const [loading, setLoading] = useState(false);
  const [labList, setLabList] = useState([]);

  const day = new Date().toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const fetchLab = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (directViewerToken) {
        res = await patientApi.getLabInformationByViewerToken(directViewerToken);
        if (!res || res?.data?.success === false) {
          navigate("/miniapp/404", { replace: true });
          return;
        }
      } else if (appToken) {
        const authRes = await patientApi.authenticateViewerToken(appToken);
        if (!authRes || authRes?.data?.success === false) {
          navigate("/miniapp/404", { replace: true });
          return;
        }
        const viewerToken = authRes.data.viewer_token;
        res = await patientApi.getLabInformationByViewerToken(viewerToken);
        if (!res || res?.data?.success === false) {
          navigate("/miniapp/404", { replace: true });
          return;
        }
      } else {
        navigate("/miniapp/404", { replace: true });
        return;
      }

      if (res?.data?.success === true && res?.data?.lab_list?.length > 0) {
        const unique = [...new Map(res.data.lab_list.map((i) => [i.expr2, i])).values()];
        setLabList(unique);
      }
    } catch (e) {
      console.error(e);
      navigate("/miniapp/404", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [appToken, directViewerToken, navigate]);

  useEffect(() => {
    fetchLab();
  }, [fetchLab]);


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#4fc3c8] via-[#3da8bf] to-[#5b8dd9] px-5 pt-12 pb-8 flex flex-col items-center">
        <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mb-4 shadow-lg">
          <img src={logo} alt="Phukphan Logo" className="w-16 h-16 object-contain" />
        </div>
        <p className="text-white font-bold text-3xl tracking-wide">ผูกพันธุ์</p>
        <p className="text-white/80 text-sm mt-1 text-center">ตรวจสอบผลการตรวจทางห้องปฏิบัติการ</p>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-4">
        <p className="text-xs text-gray-500 mb-4">
          <FontAwesomeIcon icon={faClock} className="mr-1" />
          ข้อมูลประจำวันที่ {day}
        </p>

        {loading ? (
          <div className="flex flex-col items-center justify-center mt-20 gap-3">
            <HashLoader color="#0d9488" size={50} />
            <p className="text-teal-600 text-sm font-medium">กำลังโหลด...</p>
          </div>
        ) : (
          <>
            {/* Section: ยีนแพ้ยา */}
            <div
              className="flex items-center justify-between px-4 py-2.5 rounded-t-xl mb-0"
              style={{ backgroundColor: "#1a5c38" }}
            >
              <p className="text-white font-bold text-sm">ยีนแพ้ยา</p>
              <p className="text-white/70 text-xs">จำนวน {labList.length} รายการ</p>
            </div>
            <div className="mb-5">
              {labList.length === 0 ? (
                <div className="bg-white border border-t-0 border-gray-100 rounded-b-xl px-4 py-6 text-center text-gray-400 text-sm">
                  ไม่พบข้อมูล
                </div>
              ) : (
                labList.map((item, index) => (
                  <ResultCard key={item._id || index} item={item} index={index} isLast={index === labList.length - 1} />
                ))
              )}
            </div>

            {/* Section: แม่และเด็ก */}
            <div
              className="flex items-center justify-between px-4 py-2.5 rounded-t-xl mb-0"
              style={{ backgroundColor: "#1a5c38" }}
            >
              <p className="text-white font-bold text-sm">แม่และเด็ก</p>
              <p className="text-white/70 text-xs">จำนวน {MOCK_MOTHER_CHILD_DATA.length} รายการ</p>
            </div>
            <div className="mb-5">
              {MOCK_MOTHER_CHILD_DATA.map((item, index) => (
                <ResultCard key={item._id || index} item={item} index={index} isLast={index === MOCK_MOTHER_CHILD_DATA.length - 1} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="py-4 text-center">
        <p className="text-xs text-gray-400">ตรวจสอบผลการตรวจทางห้องปฏิบัติการ</p>
      </div>
    </div>
  );
}

export default MiniAppLaboratoryResult;
